import { SystemActionProgressBanner } from "#src/batteries-included-components/Banners/SystemActionProgressBanner";
import WorkflowCommentsDrawer from "#src/batteries-included-components/Drawers/Workflows/WorkflowCommentsDrawer/WorkflowCommentsDrawer";
import {
  getChoicesFromWorkflowConfiguration,
  getDatasetDetailsFromWorkflowConfiguration,
  getEventSchemaIdFromWorkflowSystemActionConfiguration,
  getEventSchemaIdFromWorkflowUserTaskConfiguration,
  getFormSchemaIdFromWorkflowConfiguration,
  getRecordFilterFromWorkflowConfiguration,
  getReportIdFromWorkflowConfiguration,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowTaskDetailDrawer/WorkflowTaskDetailDrawer.helpers";
import { WorkflowStepStatusPill } from "#src/batteries-included-components/PillsCollection/WorkflowStepStatusPill";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import { useListUserGroups } from "#src/components/hooks/adapters/useUserGroups";
import { useGetOneWorkflow } from "#src/components/hooks/adapters/useWorkflows";
import {
  useGetOneWorkflowTask,
  useListWorkflowActions,
} from "#src/components/hooks/adapters/useWorkflowTasks";
import {
  useCheckCommentPermissions,
  useCommentsStore,
} from "#src/components/hooks/useComments";
import {
  getWorkflowTaskIconFromType,
  getWorkflowTaskNameFromType,
} from "#src/components/Workflows/WorkflowsHelpers";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import useWorkflowTriggersKeyValueData from "#src/hooks/useWorkflowTriggersKeyValueData";
import {
  getWorkflowDisplayVariables,
  getWorkflowLookupIdsByEntityType,
} from "#src/routes/workflows/all/[workflowId]/detail/WorkflowDetailKeyValuePanel.helpers";
import { renderWorkflowDisplayVariable } from "#src/components/Workflows/WorkflowVariableDisplay";
import { useMultiLookupAssetDetails } from "#src/utils/lookups";
import { getRelativeTimeDifference } from "#utils/date";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  Button,
  ButtonWithPopover,
  Drawer,
  Icon,
  KeyValueList,
  Pill,
  Skeleton,
  TimelinePanel,
  Tooltip,
  useTimelinePanelStore,
  useToast,
} from "@validereinc/common-components";
import {
  FormSubmissionStatus,
  FormSubmissionStatusType,
  FormSubmissionTaskType,
  Resources,
  SystemActionTypeType,
  TransactionAdapter,
  TransactionStatus,
  UserTaskType,
  UserTaskTypeType,
  WorkflowStepType,
  WorkflowSubmitDatasetTaskMetadata,
  WorkflowTaskAdapter,
  WorkflowTaskStatus,
  WorkflowTaskType,
  WorkflowTaskTypes,
  WorkflowUserTaskAttachmentType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import { datetimeFormatter } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { upperFirst } from "es-toolkit/compat";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import TaskCommentsDrawer from "../TaskCommentsDrawer/TaskCommentsDrawer";
import { WorkflowActionAssessTemplatedReportForComments } from "./WorkflowActionAssessTemplatedReportForComments/WorkflowActionAssessTemplatedReportForComments";
import { WorkflowActionCreateEvent } from "./WorkflowActionCreateEvent/WorkflowActionCreateEvent";
import { WorkflowActionDelay } from "./WorkflowActionDelay";
import { WorkflowActionRunWorker } from "./WorkflowActionRunWorker/WorkflowActionRunWorker";
import { WorkflowActionUpdateRecordStatus } from "./WorkflowActionUpdateRecordStatus";
import {
  FormSubmissionAttachmentHeader,
  getAttachmentTitle,
  RecordAttachmentHeader,
  ReportAttachment,
  WorkflowTaskAttachment,
} from "./WorkflowTaskAttachment";
import { WorkflowTaskCompleteEvent } from "./WorkflowTaskCompleteEvent";
import styles from "./WorkflowTaskDetailDrawer.module.scss";
import { WorkflowTaskDetailKeyValueList } from "./WorkflowTaskDetailKeyValueList";
import { WorkflowTaskFormSubmission } from "./WorkflowTaskFormSubmission";
import { WorkflowTaskSubmitDataset } from "./WorkflowTaskSubmitDataset";
import { WorkflowTaskUserChoice } from "./WorkflowTaskUserChoice";

const { flattenWorkflowSteps } = Workflows.logic;

const { AccordionPanel } = Accordion;
const cx = classNames.bind(styles);

type WorkflowDetailsDrawerProps = {
  setSelectedTask: React.Dispatch<
    React.SetStateAction<WorkflowTaskType | null>
  >;
  selectedTask: WorkflowTaskType | null;
  onReassignTask?: (task: WorkflowTaskType) => void;
  taskStepIds: string[];
  disableTaskNavigation?: boolean;
  shouldDisplayWorkflowTriggers?: boolean;
};

const TASK_COMMENTS_DRAWER_KEY = "task-comments";
const WORKFLOW_COMMENTS_DRAWER_KEY = "workflow-comments";
const validCompletedFormStatus: FormSubmissionStatusType[] = [
  FormSubmissionStatus.PENDING,
  FormSubmissionStatus.SUBMITTED,
  FormSubmissionStatus.VALIDATED,
  FormSubmissionStatus.INVALIDATED,
];

export const WorkflowTaskDetailDrawer = ({
  setSelectedTask,
  selectedTask,
  onReassignTask,
  taskStepIds,
  disableTaskNavigation,
  shouldDisplayWorkflowTriggers,
}: WorkflowDetailsDrawerProps) => {
  const {
    v2: {
      userInfo: { user },
    },
  } = useAuthenticatedContext();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const {
    isOpen: isTaskCommentsDrawerOpen,
    setIsOpen: setIsTaskCommentsDrawerOpen,
    setTargetDrawer: setTaskCommentsTargetDrawer,
  } = useTimelinePanelStore();
  const {
    isOpen: isWorkflowCommentsDrawerOpen,
    setIsOpen: setIsWorkflowCommentsDrawerOpen,
    setTargetDrawer: setWorkflowCommentsTargetDrawer,
  } = useTimelinePanelStore();
  const { commentCounts } = useCommentsStore();
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);

  const workflowDetailsEnabled = Boolean(selectedTask?.workflow_id);
  const workflowId = selectedTask?.workflow_id ?? "";
  const workflowQuery = useGetOneWorkflow(
    { id: workflowId },
    { enabled: Boolean(workflowId) }
  );
  const workflowDetails = workflowQuery.data?.data;
  const workflowDetailsLoading = workflowQuery.isLoading;
  const { canReadComments, canReadAssetType, hasWorkflowTaskCommentsAccess } =
    useCheckCommentPermissions(workflowDetails?.asset?.asset_type);

  const [canReassignTask] = useIsFeatureAvailable({
    featureFlagQuery: `core:workflows`,
    permissionQuery: `workflow_tasks:assign`,
  });

  const filters: Parameters<typeof WorkflowTaskAdapter.getList>[0]["filters"] =
    {
      workflow_id: workflowId,
    };
  const allWorkflowTasksPayload = {
    filters: filters,
  };

  //@ts-expect-error doesn't like null filters on assignee_user
  const actionsQuery = useListWorkflowActions(allWorkflowTasksPayload, {
    enabled: !!workflowId,
  });

  const taskId = selectedTask?.id ?? "";
  const isSystemAction = selectedTask?.step_type === "system_action";
  const isTaskQueryEnabled = Boolean(taskId) && !isSystemAction;

  const workflowTaskQuery = useGetOneWorkflowTask(
    { id: taskId },
    { enabled: isTaskQueryEnabled }
  );
  const workflowTaskDetails = workflowTaskQuery.data?.data;
  const workflowTaskDetailsLoading = isTaskQueryEnabled
    ? workflowTaskQuery.isLoading
    : false;

  //assume only one submission at a time for now
  const datasetSubmissionId = useMemo(() => {
    if (
      workflowTaskDetails?.type === WorkflowTaskTypes.SUBMIT_DATASET &&
      workflowTaskDetails?.metadata
    ) {
      const metadata =
        workflowTaskDetails.metadata as WorkflowSubmitDatasetTaskMetadata;
      if (metadata?.dataset_submission_ids?.length) {
        return metadata.dataset_submission_ids[0];
      } else return null;
    }
    return null;
  }, [workflowTaskDetails]);
  const { data: transactionData } = useQuery(
    [Resources.TRANSACTION, datasetSubmissionId],
    () =>
      TransactionAdapter.getOne({
        id: datasetSubmissionId ?? "",
        meta: { history: false },
      }),
    {
      enabled: Boolean(datasetSubmissionId),
    }
  );
  const datasetTransaction = transactionData?.data[0];

  const formSchemaId = getFormSchemaIdFromWorkflowConfiguration(
    selectedTask?.step_id,
    workflowDetails?.config
  );

  const { datasetId, datasetRequired } =
    getDatasetDetailsFromWorkflowConfiguration(
      selectedTask?.step_id,
      workflowDetails?.config
    );

  const userTaskEventSchemaId =
    getEventSchemaIdFromWorkflowUserTaskConfiguration(
      selectedTask?.step_id,
      workflowDetails?.config
    );

  const systemActionEventSchemaId =
    getEventSchemaIdFromWorkflowSystemActionConfiguration(
      selectedTask?.step_id,
      workflowDetails?.config
    );

  const formSchemaQuery = useGetOneFormSchema(
    { id: formSchemaId ?? "" },
    { enabled: Boolean(formSchemaId) }
  );
  const formSchemaDetails = formSchemaQuery.data?.data;

  const reportId = getReportIdFromWorkflowConfiguration(
    selectedTask?.step_id,
    workflowDetails?.config
  );

  const { data: userGroupsData } = useListUserGroups(
    { filters: { "user.id": [user?.id] } },
    { enabled: !!user }
  );
  const userGroups = userGroupsData?.data ?? [];

  const { isLoading: isLoadingComplete, mutate: completeTask } = useMutation({
    mutationFn: async () => {
      if (!selectedTask || !workflowTaskDetails) {
        return;
      }

      await WorkflowTaskAdapter.updateOne({
        id: selectedTask.id,
        data: {
          status: WorkflowTaskStatus.COMPLETE,
          ...(selectedChoiceId ? { choice: selectedChoiceId } : {}),
          ...((
            workflowTaskDetails.metadata as WorkflowSubmitDatasetTaskMetadata
          ).dataset_submission_ids
            ? {
                dataset_submission_ids: (
                  workflowTaskDetails.metadata as WorkflowSubmitDatasetTaskMetadata
                ).dataset_submission_ids,
              }
            : {}),
        },
        previousData: selectedTask,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Resources.WORKFLOW] });
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW],
        });
      }, 4000);
      onClose();
      toast.push({
        intent: "success",
        description: "Successfully updated task status to complete",
      });
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to update status. Please try again.",
      });
    },
  });

  const { isLoading: isLoadingDismissed, mutate: dismissTask } = useMutation({
    mutationFn: async () => {
      if (!selectedTask) {
        return;
      }

      await WorkflowTaskAdapter.updateOne({
        id: selectedTask.id,
        data: {
          status: WorkflowTaskStatus.DISMISSED,
        },
        previousData: selectedTask,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Resources.WORKFLOW] });
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW],
        });
      }, 4000);
      toast.push({
        intent: "success",
        description: "Successfully updated task status to dismissed",
      });
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to dismiss task.",
      });
    },
  });

  const onClose = () => {
    setSelectedTask(null);
    setIsTaskCommentsDrawerOpen(false);
    setIsWorkflowCommentsDrawerOpen(false);
    setTaskCommentsTargetDrawer(null);
    setWorkflowCommentsTargetDrawer(null);
  };

  const choices = useMemo(
    () =>
      getChoicesFromWorkflowConfiguration(
        workflowTaskDetails?.step_id,
        workflowDetails?.config
      ),
    [workflowTaskDetails?.step_id, workflowDetails?.config]
  );

  const allWorkflowSteps = useMemo(
    () => flattenWorkflowSteps(workflowDetails?.config.steps),
    [workflowDetails?.config.steps]
  );

  const getAttachments = useCallback(() => {
    const stepId = workflowTaskDetails?.step_id;
    if (!stepId) return [];

    const step = allWorkflowSteps[stepId];
    if (step && step.type === WorkflowStepType.USER_TASK) {
      return step.task?.attachments ?? [];
    }
    return [];
  }, [workflowTaskDetails, allWorkflowSteps]);
  const attachments = useMemo(() => getAttachments(), [getAttachments]);

  useEffect(() => {
    if (choices.length > 0) {
      if (
        workflowTaskDetails?.status === WorkflowTaskStatus.OPEN ||
        workflowTaskDetails?.status === WorkflowTaskStatus.OVERDUE
      ) {
        setSelectedChoiceId(choices[0].id);
      } else if (workflowTaskDetails?.status === WorkflowTaskStatus.COMPLETE) {
        const selectedChoice = choices.find(
          (choice) => choice.next && taskStepIds.includes(choice.next)
        );
        setSelectedChoiceId(selectedChoice?.id ?? null);
      }
    } else {
      setSelectedChoiceId(null);
    }
  }, [choices]);

  const isLoading = isLoadingComplete || isLoadingDismissed;

  const isTaskFormRequiredWithNoRelevantSubmissions = useMemo(() => {
    const workflowTask = workflowTaskDetails ?? selectedTask;
    const hasTaskAnySubmissionsOfSameSchema =
      !!workflowTask &&
      workflowTask?.type === WorkflowTaskTypes.SUBMIT_FORM &&
      Array.isArray(workflowTask?.form_submission) &&
      workflowTask?.form_submission.length > 0 &&
      workflowTask?.form_submission.some((submission) => {
        return submission.form_schema.id === formSchemaId;
      });

    const doesTaskHaveAnyValidSubmissions =
      !!workflowTask?.form_submission?.some((submission) =>
        validCompletedFormStatus.includes(submission.status)
      );

    const doesCurrentStepRequireFormSubmission = !!(
      allWorkflowSteps[selectedTask?.step_id ?? ""]
        ?.task as FormSubmissionTaskType
    )?.form_required;

    return (
      doesCurrentStepRequireFormSubmission &&
      (!hasTaskAnySubmissionsOfSameSchema || !doesTaskHaveAnyValidSubmissions)
    );
  }, [workflowTaskDetails, workflowDetails, selectedTask, formSchemaId]);

  const isDataSubmissionRequiredWithNoRelevantSubmissions = useMemo(() => {
    if (!workflowTaskDetails) {
      return true;
    }

    if (
      workflowTaskDetails?.type !== WorkflowTaskTypes.SUBMIT_DATASET ||
      !datasetRequired
    ) {
      return false;
    }

    const submissionIds = (
      workflowTaskDetails.metadata as WorkflowSubmitDatasetTaskMetadata
    )?.dataset_submission_ids;

    return (
      !submissionIds ||
      submissionIds.length === 0 ||
      (datasetTransaction?.status !== TransactionStatus.COMPLETED &&
        datasetTransaction?.status !==
          TransactionStatus.COMPLETED_WITH_PARTIAL_SUCCESS)
    );
  }, [workflowTaskDetails, datasetRequired, datasetTransaction?.status]);

  const checkIfUserAssignedToTask = () => {
    const isUserInAssigneeGroup = userGroups.some(
      (userGroup) => userGroup.id === selectedTask?.assignee_group
    );
    const isUserAssignee = user?.id === selectedTask?.assignee_user;

    return isUserAssignee || isUserInAssigneeGroup;
  };

  const commentCount =
    (hasWorkflowTaskCommentsAccess
      ? commentCounts[`taskComments-${taskId}`]
      : commentCounts[`workflowComments-${workflowId}`]) || 0;

  const isTaskAssigneeRestrictedAndUserNotAssignee =
    (selectedTask?.restrict_assignee_completion &&
      !checkIfUserAssignedToTask()) ??
    false;

  const isCompleteButtonDisabled =
    (workflowDetailsEnabled && workflowDetailsLoading) ||
    (selectedTask?.status !== WorkflowTaskStatus.OPEN &&
      selectedTask?.status !== WorkflowTaskStatus.OVERDUE) ||
    isTaskFormRequiredWithNoRelevantSubmissions ||
    isTaskAssigneeRestrictedAndUserNotAssignee ||
    isDataSubmissionRequiredWithNoRelevantSubmissions;

  const completeButton = (
    <ButtonWithPopover
      key="complete-task-button"
      label={
        isTaskAssigneeRestrictedAndUserNotAssignee
          ? "Only assignees can action this task"
          : ""
      }
      variant="primary"
      onClick={() => completeTask()}
      isLoading={isLoading}
      buttonProps={{
        disabled: isCompleteButtonDisabled,
      }}
      popoverProps={{ positions: ["top"] }}
    >
      Complete
    </ButtonWithPopover>
  );

  const completeButtonComponent = () => {
    if (isTaskFormRequiredWithNoRelevantSubmissions) {
      return (
        <Tooltip
          content={`At least one form submission ${formSchemaDetails?.name ? `of form template "${formSchemaDetails?.name}"` : ""} with a status of ${validCompletedFormStatus
            .map((s, idx) => {
              if (idx === validCompletedFormStatus.length - 1) {
                return `or ${upperFirst(s)}`;
              }
              return upperFirst(s);
            })
            .join(", ")} is required to complete the task.`}
        >
          {completeButton}
        </Tooltip>
      );
    }
    if (isDataSubmissionRequiredWithNoRelevantSubmissions) {
      return (
        <Tooltip
          content={`You must upload a successful dataset to complete this task.`}
        >
          {completeButton}
        </Tooltip>
      );
    }
    return completeButton;
  };

  const reassignButton = (
    <Button
      isLoading={isLoading}
      onClick={() => {
        if (selectedTask) {
          onReassignTask?.(selectedTask);
          setSelectedTask(null);
        }
      }}
      disabled={
        !canReassignTask ||
        (selectedTask?.status !== WorkflowTaskStatus.OPEN &&
          selectedTask?.status !== WorkflowTaskStatus.OVERDUE)
      }
    >
      Re-Assign
    </Button>
  );
  const reAssignButtonComponent = canReassignTask ? (
    reassignButton
  ) : (
    <Tooltip
      content={`You do not have the permissions to Re-Assign this task.`}
    >
      {reassignButton}
    </Tooltip>
  );

  const [prevTask, nextTask] = useMemo(() => {
    const currTaskIndex = actionsQuery.data?.data.findIndex(
      (task) => task.id === selectedTask?.id
    );
    if (currTaskIndex !== undefined && currTaskIndex !== -1) {
      return [
        actionsQuery.data?.data[currTaskIndex + 1],
        actionsQuery.data?.data[currTaskIndex - 1],
      ];
    } else {
      return [undefined, undefined];
    }
  }, [actionsQuery.data?.data, selectedTask]);

  const isStepSystemAction =
    allWorkflowSteps[selectedTask?.step_id ?? ""]?.type ===
    WorkflowStepType.SYSTEM_ACTION;
  const workflowStepType =
    allWorkflowSteps[selectedTask?.step_id ?? ""]?.task.type;

  const workflowStepStatus = selectedTask?.status;

  const isUserTask =
    selectedTask?.type &&
    Object.values(UserTaskType).includes(selectedTask.type as UserTaskTypeType);

  const descriptorFooter = useMemo(() => {
    const details = workflowTaskDetails ?? selectedTask;

    if (!details) return undefined;

    if (details.status !== "complete" && details.due_date) {
      return getRelativeTimeDifference(new Date(details.due_date), new Date());
    }

    if (details.type === "delay") {
      const delayDate = Date.parse(details.description);
      if (!isNaN(delayDate)) {
        const now = Date.now();
        const timeRemaining = delayDate - now;
        if (timeRemaining <= 0) {
          return `Completed ${datetimeFormatter(new Date(details.description))}`;
        } else {
          return `Created ${datetimeFormatter(new Date(details.created_at))}`;
        }
      }
    }

    return undefined;
  }, [workflowTaskDetails, selectedTask]);

  const recordFilter = getRecordFilterFromWorkflowConfiguration(
    selectedTask?.step_id,
    workflowDetails?.config
  );

  const {
    formTriggerDetails,
    isLoading: isLoadingWorkflowTriggers,
    trigger,
    areThereAnyTriggers,
  } = useWorkflowTriggersKeyValueData(
    shouldDisplayWorkflowTriggers ? workflowDetails : undefined
  );

  const workflowTaskDisplayVariables = useMemo(() => {
    const step = allWorkflowSteps[selectedTask?.step_id ?? ""];
    if (step?.type !== WorkflowStepType.USER_TASK) {
      return [];
    }

    return getWorkflowDisplayVariables(
      workflowDetails,
      step.variables_to_display
    );
  }, [workflowDetails, allWorkflowSteps, selectedTask?.step_id]);

  const workflowTaskLookupIdsByEntityType = useMemo(
    () => getWorkflowLookupIdsByEntityType(workflowTaskDisplayVariables),
    [workflowTaskDisplayVariables]
  );

  const {
    assetDetailsMap: lookupAssetDetailsById,
    isLoading: isLoadingWorkflowInformationLookups,
  } = useMultiLookupAssetDetails(workflowTaskLookupIdsByEntityType);

  const workflowInformationRows = useMemo(
    () => [
      ...(shouldDisplayWorkflowTriggers ? trigger : []),
      ...workflowTaskDisplayVariables.map((variable) => ({
        title: variable.displayName,
        value: renderWorkflowDisplayVariable(variable, lookupAssetDetailsById),
      })),
    ],
    [
      shouldDisplayWorkflowTriggers,
      trigger,
      workflowTaskDisplayVariables,
      lookupAssetDetailsById,
    ]
  );

  const showWorkflowInformation =
    workflowInformationRows.length > 0 ||
    (Boolean(shouldDisplayWorkflowTriggers) && areThereAnyTriggers);

  return (
    <Drawer
      size="md"
      isOpen={!!selectedTask}
      shouldAllowClose={!isTaskCommentsDrawerOpen}
      onClose={onClose}
      title={isStepSystemAction ? "Automation Details" : "User Task Details"}
      colorVariant={isStepSystemAction ? "teal" : "stone"}
      iconActionLeft={
        isStepSystemAction ? (
          <Icon
            variant="sparkle"
            weight="fill"
          />
        ) : undefined
      }
      titleAction={
        !disableTaskNavigation ? (
          <Drawer.NextPrevButtons
            onNextClick={() => {
              if (nextTask) {
                setSelectedTask(nextTask);
              }
            }}
            onPrevClick={() => {
              if (prevTask) {
                setSelectedTask(prevTask);
              }
            }}
            isNextDisabled={!nextTask}
            isPrevDisabled={!prevTask}
          />
        ) : undefined
      }
      actionRow={
        <div className={cx("buttonContainer")}>
          {canReadComments && canReadAssetType && workflowTaskDetails ? (
            <TimelinePanel.Trigger
              targetDrawer={
                hasWorkflowTaskCommentsAccess
                  ? TASK_COMMENTS_DRAWER_KEY
                  : WORKFLOW_COMMENTS_DRAWER_KEY
              }
            >
              {({ onClick }) => (
                <Button
                  className={cx("commentsButton", {
                    "commentsButton--active":
                      isTaskCommentsDrawerOpen || isWorkflowCommentsDrawerOpen,
                  })}
                  onClick={onClick}
                  slotLeft={
                    <Icon
                      variant="chat-text"
                      size={24}
                    />
                  }
                  slotRight={<Pill variant="default">{commentCount}</Pill>}
                />
              )}
            </TimelinePanel.Trigger>
          ) : null}
          {selectedTask?.is_dismissible ? (
            <Button
              isLoading={isLoading}
              variant="error-outline"
              onClick={() => dismissTask()}
              disabled={
                selectedTask?.status !== WorkflowTaskStatus.OPEN &&
                selectedTask?.status !== WorkflowTaskStatus.OVERDUE
              }
            >
              Dismiss
            </Button>
          ) : null}
          {selectedTask ? reAssignButtonComponent : null}
          {selectedTask?.status === WorkflowTaskStatus.COMPLETE ||
          selectedTask?.status === WorkflowTaskStatus.MISSED ? (
            <Button
              isLoading={isLoading}
              onClick={() => {
                setSelectedTask(null);
              }}
              variant="primary"
            >
              Done
            </Button>
          ) : (
            <>{completeButtonComponent()}</>
          )}
        </div>
      }
      rightSlot={
        canReadComments &&
        canReadAssetType &&
        workflowDetails &&
        workflowTaskDetails ? (
          hasWorkflowTaskCommentsAccess ? (
            <TaskCommentsDrawer
              workflow={workflowDetails}
              task={workflowTaskDetails}
              drawerKey={TASK_COMMENTS_DRAWER_KEY}
            />
          ) : (
            <WorkflowCommentsDrawer
              workflow={workflowDetails}
              drawerKey={WORKFLOW_COMMENTS_DRAWER_KEY}
            />
          )
        ) : undefined
      }
    >
      <div className={cx("contentContainer")}>
        <Drawer.DescriptorWithIcon
          isLoading={workflowTaskDetailsLoading}
          title={[
            ...(selectedTask?.name
              ? [<span key="task-name">{selectedTask.name}</span>]
              : []),
            ...(workflowDetails && actionsQuery.data?.data && selectedTask
              ? [
                  <WorkflowStepStatusPill
                    workflowStep={selectedTask}
                    isLoading={workflowTaskDetailsLoading}
                    shouldShowIcon={false}
                    key="status-pill"
                  />,
                ]
              : []),
          ]}
          titleHeader={getWorkflowTaskNameFromType(selectedTask?.type)}
          titleFooter={descriptorFooter}
          iconProps={{
            variant: getWorkflowTaskIconFromType(selectedTask?.type),
            containerClassName: isUserTask
              ? cx({
                  descriptorIcon: true,
                  descriptorIconOpen:
                    workflowTaskDetails?.status === WorkflowTaskStatus.OPEN,
                  descriptorIconOverdue:
                    workflowTaskDetails?.status === WorkflowTaskStatus.OVERDUE,
                  descriptorIconComplete:
                    workflowTaskDetails?.status === WorkflowTaskStatus.COMPLETE,
                  descriptorIconDismissed:
                    workflowTaskDetails?.status ===
                    WorkflowTaskStatus.DISMISSED,
                })
              : cx("descriptorIcon"),
          }}
        />
        {isStepSystemAction &&
        workflowStepStatus === WorkflowTaskStatus.OPEN &&
        workflowStepType ? (
          <SystemActionProgressBanner
            workflowStepType={workflowStepType as SystemActionTypeType}
          />
        ) : null}
        {selectedTask?.type === WorkflowTaskTypes.CHOICE &&
        selectedChoiceId &&
        workflowTaskDetails ? (
          <Skeleton
            isLoading={workflowTaskDetailsLoading}
            style={{ minHeight: 200 }}
          >
            <WorkflowTaskUserChoice
              choices={choices}
              selectedChoiceId={selectedChoiceId}
              setSelectedChoiceId={setSelectedChoiceId}
              isDisabled={
                workflowTaskDetails.status === WorkflowTaskStatus.COMPLETE
              }
            />
          </Skeleton>
        ) : null}
        {selectedTask?.type === WorkflowTaskTypes.SUBMIT_FORM ? (
          <WorkflowTaskFormSubmission
            formSchemaId={formSchemaId}
            workflowTask={workflowTaskDetails ?? selectedTask}
            workflow={workflowDetails}
          />
        ) : null}
        {selectedTask?.type === WorkflowTaskTypes.SUBMIT_DATASET ? (
          <Skeleton
            isLoading={true}
            style={{ minHeight: 135 }}
          >
            {workflowTaskDetails && (
              <WorkflowTaskSubmitDataset
                datasetId={datasetId}
                workflowTask={workflowTaskDetails}
              />
            )}
          </Skeleton>
        ) : null}
        {selectedTask?.type === WorkflowTaskTypes.COMPLETE_EVENT ? (
          <WorkflowTaskCompleteEvent
            workflowTask={workflowTaskDetails ?? selectedTask}
            workflow={workflowDetails}
            eventSchemaId={userTaskEventSchemaId}
          />
        ) : null}
        {selectedTask?.type === WorkflowTaskTypes.DELAY ? (
          <WorkflowActionDelay
            workflowTask={workflowTaskDetails ?? selectedTask}
            workflowConfiguration={workflowDetails?.config}
            isLoading={workflowTaskDetailsLoading}
          />
        ) : null}
        {selectedTask?.type === WorkflowTaskTypes.CREATE_EVENT ? (
          <WorkflowActionCreateEvent
            eventSchemaId={systemActionEventSchemaId ?? ""}
          />
        ) : null}
        {(selectedTask?.type === WorkflowTaskTypes.LOCK_RECORD ||
          selectedTask?.type === WorkflowTaskTypes.UNLOCK_RECORD) && (
          <WorkflowActionUpdateRecordStatus recordFilter={recordFilter ?? {}} />
        )}
        {selectedTask?.type ===
          WorkflowTaskTypes.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS && (
          <WorkflowActionAssessTemplatedReportForComments
            reportId={reportId ?? ""}
          />
        )}
        {selectedTask?.type === WorkflowTaskTypes.RUN_WORKER && (
          <WorkflowActionRunWorker
            workflowTask={selectedTask}
            workflowConfiguration={workflowDetails?.config}
            isLoading={workflowDetailsLoading}
          />
        )}
        {attachments.filter(
          (a) => a.type !== WorkflowUserTaskAttachmentType.REPORT
        ).length > 0 && (
          <Accordion defaultActiveKeys={["attachment.0"]}>
            {attachments.map((attachment, index) => (
              <AccordionPanel
                key={`attachment.${index}`}
                dataKey={`attachment.${index}`}
                title={getAttachmentTitle(attachment)}
                actionRow={
                  <>
                    {attachment.type ===
                    WorkflowUserTaskAttachmentType.RECORD ? (
                      <RecordAttachmentHeader
                        taskId={taskId}
                        attachment={attachment}
                      />
                    ) : null}
                    {attachment.type ===
                    WorkflowUserTaskAttachmentType.FORM_SUBMISSION ? (
                      <FormSubmissionAttachmentHeader attachment={attachment} />
                    ) : null}
                  </>
                }
              >
                <WorkflowTaskAttachment attachment={attachment} />
              </AccordionPanel>
            ))}
          </Accordion>
        )}
        {attachments.filter(
          (a) => a.type === WorkflowUserTaskAttachmentType.REPORT
        ).length > 0 && (
          <Accordion defaultActiveKeys={["report-attachments"]}>
            <AccordionPanel
              dataKey={"report-attachments"}
              title={"Reports to Review"}
            >
              <ReportAttachment
                attachments={attachments.filter(
                  (a) => a.type === WorkflowUserTaskAttachmentType.REPORT
                )}
                taskId={taskId}
              />
            </AccordionPanel>
          </Accordion>
        )}
        <Accordion
          key={"overview"}
          defaultActiveKeys={["overview"]}
        >
          <AccordionPanel
            dataKey={"overview"}
            title={isStepSystemAction ? "Automation Overview" : "Task Overview"}
          >
            <WorkflowTaskDetailKeyValueList
              isLoading={workflowTaskDetailsLoading}
              workflowDetails={workflowDetails}
              type={selectedTask?.type}
              priorityId={selectedTask?.priority_id}
              priorityLabel={selectedTask?.priority_label}
              assignee_user={selectedTask?.assignee_user ?? undefined}
              assignee_group={selectedTask?.assignee_group ?? undefined}
              updated_by={selectedTask?.updated_by ?? undefined}
              description={
                selectedTask?.type === "delay"
                  ? undefined
                  : selectedTask?.description
              }
              updated_at={selectedTask?.updated_at}
            />
          </AccordionPanel>
        </Accordion>
        {showWorkflowInformation && (
          <Skeleton
            isLoading={
              isLoadingWorkflowTriggers || isLoadingWorkflowInformationLookups
            }
          >
            {!isLoadingWorkflowTriggers &&
              !isLoadingWorkflowInformationLookups && (
                <Accordion
                  key="trigger_information"
                  defaultActiveKeys={[
                    "trigger_information",
                    "form_trigger_details",
                  ]}
                >
                  <AccordionPanel
                    dataKey="trigger_information"
                    title="Workflow Information"
                  >
                    <KeyValueList
                      data={workflowInformationRows}
                      variant="shaded"
                    />
                  </AccordionPanel>
                  {formTriggerDetails.length > 0 && (
                    <AccordionPanel
                      dataKey="form_trigger_details"
                      title="Triggering Form Questions & Answers"
                    >
                      <KeyValueList
                        data={formTriggerDetails}
                        variant="shaded"
                      />
                    </AccordionPanel>
                  )}
                </Accordion>
              )}
          </Skeleton>
        )}
      </div>
    </Drawer>
  );
};
